import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Download, Printer,
  FileText, DollarSign
} from 'lucide-react';

const agingTone = {
  success: 'bg-success-50 text-success-700 ring-1 ring-success-100 dark:bg-success-900/25 dark:text-success-400 dark:ring-success-900/60',
  brand: 'bg-primary-50 text-primary-900 ring-1 ring-primary-100 dark:bg-primary-950/70 dark:text-primary-300 dark:ring-primary-900',
  warning: 'bg-warning-50 text-warning-700 ring-1 ring-warning-100 dark:bg-warning-900/25 dark:text-warning-400 dark:ring-warning-900/60',
  danger: 'bg-danger-50 text-danger-700 ring-1 ring-danger-100 dark:bg-danger-900/25 dark:text-danger-400 dark:ring-danger-900/60',
};

function AgingSummaryCard({ title, amount, tone = 'brand' }) {
  return (
    <div className="card flex flex-col p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase leading-snug tracking-[0.08em] text-surface-500 dark:text-surface-400">{title}</p>
        <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${agingTone[tone]}`}>
          <DollarSign size={12} />
        </span>
      </div>
      <p className="mt-2 break-words text-[15px] font-medium leading-tight tracking-[-0.2px] text-surface-900 tabular-nums dark:text-white">
        {formatCurrency(amount)}
      </p>
    </div>
  );
}

function SkeletonTableRow({ cols = 10 }) {
  return (
    <tr>
      {[...Array(cols)].map((_, i) => (
        <td key={i}><div className="skeleton h-4 w-full" /></td>
      ))}
    </tr>
  );
}

export default function AgingPage({ type }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPayable = type === 'payable';
  const apiPath = isPayable ? '/reports/aging-payable' : '/reports/aging-receivable';
  const title = isPayable ? 'Aging Utang' : 'Aging Piutang';
  const subtitle = isPayable ? 'Analisis umur utang (Aging Payable)' : 'Analisis umur piutang (Aging Receivable)';
  const partyLabel = isPayable ? 'Supplier' : 'Pelanggan';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath);
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const getAgingItems = () => {
    if (!data?.aging) return [];
    const items = [];
    const bucketMap = {
      current: { label: 'Belum Jatuh Tempo', min: -Infinity, max: 0 },
      '1_30': { label: '1-30 Hari', min: 1, max: 30 },
      '31_60': { label: '31-60 Hari', min: 31, max: 60 },
      '61_90': { label: '61-90 Hari', min: 61, max: 90 },
      over_90: { label: '>90 Hari', min: 91, max: Infinity },
    };

    for (const [bucket, entries] of Object.entries(data.aging)) {
      for (const entry of entries) {
        items.push({
          ...entry,
          agingBucket: bucket,
          agingLabel: bucketMap[bucket]?.label || bucket,
          partyName: isPayable ? entry.supplier_name : entry.customer_name,
          invoiceNumber: entry.invoice_number || entry.doc_number || '-',
          invoiceDate: entry.invoice_date || entry.date || entry.created_at,
          dueDate: entry.due_date,
          amount: parseFloat(entry.amount || entry.total_amount || entry.remaining_amount || 0),
          remainingAmount: parseFloat(entry.remaining_amount || entry.amount || 0),
        });
      }
    }

    // Sort by days overdue descending
    items.sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
    return items;
  };

  const getAgingValue = (item, bucket) => {
    const days = item.days_overdue || 0;
    const amount = item.remainingAmount || 0;
    if (bucket === 'current') return days <= 0 ? amount : 0;
    if (bucket === '1_30') return days >= 1 && days <= 30 ? amount : 0;
    if (bucket === '31_60') return days >= 31 && days <= 60 ? amount : 0;
    if (bucket === '61_90') return days >= 61 && days <= 90 ? amount : 0;
    if (bucket === 'over_90') return days > 90 ? amount : 0;
    return 0;
  };

  const items = getAgingItems();
  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn btn-outline btn-sm">
            <Printer className="h-4 w-4 mr-1.5" /> Cetak
          </button>
        </div>
      </div>

      {/* Aging Summary Cards */}
      {!loading && data && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <AgingSummaryCard title="Belum Jatuh Tempo" amount={summary.current || 0} tone="success" />
          <AgingSummaryCard title="1 - 30 Hari" amount={summary['1_30'] || 0} tone="brand" />
          <AgingSummaryCard title="31 - 60 Hari" amount={summary['31_60'] || 0} tone="warning" />
          <AgingSummaryCard title="61 - 90 Hari" amount={summary['61_90'] || 0} tone="warning" />
          <AgingSummaryCard title="> 90 Hari" amount={summary.over_90 || 0} tone="danger" />
        </div>
      )}

      {/* Report Content */}
      {loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{partyLabel}</th>
                  <th>Invoice</th>
                  <th>Tanggal</th>
                  <th>Jatuh Tempo</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">0-30</th>
                  <th className="text-right">31-60</th>
                  <th className="text-right">61-90</th>
                  <th className="text-right">90+</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => <SkeletonTableRow key={i} cols={9} />)}
              </tbody>
            </table>
          </div>
        </div>
      ) : !data || items.length === 0 ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isPayable ? 'Tidak ada utang yang belum dibayar.' : 'Tidak ada piutang yang belum dibayar.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              <FileText className="h-5 w-5 mr-2 inline" />
              Detail Aging {isPayable ? 'Utang' : 'Piutang'}
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Total: <span className="font-medium">{formatCurrency(
                Object.values(summary).reduce((a, b) => a + (parseFloat(b) || 0), 0)
              )}</span>
            </span>
          </div>

          <div className="table-container rounded-none border-0">
            <table className="table">
              <thead>
                <tr>
                  <th>{partyLabel}</th>
                  <th>Invoice #</th>
                  <th>Tanggal</th>
                  <th>Jatuh Tempo</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">0-30 Hari</th>
                  <th className="text-right">31-60 Hari</th>
                  <th className="text-right">61-90 Hari</th>
                  <th className="text-right">&gt;90 Hari</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{item.partyName || '-'}</span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-700 dark:text-gray-200">{item.invoiceNumber}</span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(item.invoiceDate)}</span>
                    </td>
                    <td>
                      <span className={`text-sm ${item.days_overdue > 0 ? 'text-danger-600 dark:text-danger-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                        {formatDate(item.dueDate)}
                      </span>
                      {item.days_overdue > 0 && (
                        <span className="ml-1 text-xs text-danger-500">({item.days_overdue} hr)</span>
                      )}
                    </td>
                    <td className="text-right">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(item.remainingAmount)}</span>
                    </td>
                    <td className="text-right">
                      {getAgingValue(item, 'current') > 0 && getAgingValue(item, '1_30') === 0 && getAgingValue(item, '31_60') === 0 && getAgingValue(item, '61_90') === 0 && getAgingValue(item, 'over_90') === 0 ? (
                        <span className="text-sm text-gray-700 dark:text-gray-300">{formatCurrency(getAgingValue(item, 'current'))}</span>
                      ) : getAgingValue(item, 'current') > 0 ? (
                        <span className="text-sm text-gray-700 dark:text-gray-300">{formatCurrency(getAgingValue(item, 'current'))}</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      {getAgingValue(item, '1_30') > 0 ? (
                        <span className="text-sm text-primary-900 dark:text-primary-400">{formatCurrency(getAgingValue(item, '1_30'))}</span>
                      ) : getAgingValue(item, '31_60') > 0 || getAgingValue(item, '61_90') > 0 || getAgingValue(item, 'over_90') > 0 || getAgingValue(item, 'current') > 0 ? (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      {getAgingValue(item, '31_60') > 0 ? (
                        <span className="text-sm text-warning-700 dark:text-warning-400">{formatCurrency(getAgingValue(item, '31_60'))}</span>
                      ) : getAgingValue(item, '61_90') > 0 || getAgingValue(item, 'over_90') > 0 || getAgingValue(item, 'current') > 0 || getAgingValue(item, '1_30') > 0 ? (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      {getAgingValue(item, '61_90') > 0 ? (
                        <span className="text-sm text-warning-600 dark:text-warning-400">{formatCurrency(getAgingValue(item, '61_90'))}</span>
                      ) : getAgingValue(item, 'over_90') > 0 || getAgingValue(item, 'current') > 0 || getAgingValue(item, '1_30') > 0 || getAgingValue(item, '31_60') > 0 ? (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                    <td className="text-right">
                      {getAgingValue(item, 'over_90') > 0 ? (
                        <span className="text-sm text-danger-700 dark:text-danger-400 font-medium">{formatCurrency(getAgingValue(item, 'over_90'))}</span>
                      ) : getAgingValue(item, 'current') > 0 || getAgingValue(item, '1_30') > 0 || getAgingValue(item, '31_60') > 0 || getAgingValue(item, '61_90') > 0 ? (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      ) : (
                        <span className="text-sm text-gray-300 dark:text-gray-600">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total Summary Row at Bottom */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="grid grid-cols-6 gap-4 text-sm">
              <div className="col-span-2">
                <span className="text-gray-600 dark:text-gray-400">Total Keseluruhan:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(Object.values(summary).reduce((a, b) => a + (parseFloat(b) || 0), 0))}
                </span>
              </div>
              <div className="text-right">
                <span className="text-gray-500 dark:text-gray-400">0-30:</span>
                <span className="ml-1 font-medium text-primary-900 dark:text-primary-400">{formatCurrency(summary['1_30'] || 0)}</span>
              </div>
              <div className="text-right">
                <span className="text-gray-500 dark:text-gray-400">31-60:</span>
                <span className="ml-1 font-medium text-warning-700 dark:text-warning-400">{formatCurrency(summary['31_60'] || 0)}</span>
              </div>
              <div className="text-right">
                <span className="text-gray-500 dark:text-gray-400">61-90:</span>
                <span className="ml-1 font-medium text-warning-600 dark:text-warning-400">{formatCurrency(summary['61_90'] || 0)}</span>
              </div>
              <div className="text-right">
                <span className="text-gray-500 dark:text-gray-400">&gt;90:</span>
                <span className="ml-1 font-medium text-danger-700 dark:text-danger-400">{formatCurrency(summary.over_90 || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
